import React, { useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import CatalogBreadcrumb from './CatalogBreadcrumb';
import HomeExhibitions, { ExhibitionSpotlight } from './HomeExhibitions';
import PageSeoHelmet from './PageSeoHelmet';
import { useExhibitions } from './useExhibitions';
import { useSiteSettings } from './SiteSettingsContext';
import { getMergedStoreCopy } from './storeCopy';
import { HOME_COPY_DEFAULTS } from './content/pageCopyDefaults';
import { exhibitionsScheduleJsonLd, uniqueCountriesFromExhibitions } from './exhibitionsDisplay';
import { brandNameFromSettings } from './siteBrand';
import './exhibitions-page.css';
import StorePageLoading from './components/StorePageLoading';
import StorefrontEmptyState from './components/StorefrontEmptyState';

/** Full exhibition schedule at /exhibitions — linked from home banner & footer. */
export default function ExhibitionsPage() {
  const exhibitions = useExhibitions();
  const { hash } = useLocation();
  const { settings: site } = useSiteSettings();
  const store = getMergedStoreCopy(site);
  const homeCopy = site.page_copy?.home || {};
  const brandName = brandNameFromSettings(site);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const eyebrow = homeCopy.exhibitions_eyebrow || HOME_COPY_DEFAULTS.exhibitions_eyebrow;
  const title = homeCopy.exhibitions_title || HOME_COPY_DEFAULTS.exhibitions_title;
  const subtitle = homeCopy.exhibitions_subtitle || HOME_COPY_DEFAULTS.exhibitions_subtitle;

  const hasShows = exhibitions.upcoming.length + exhibitions.recent.length > 0;
  const countryCount = useMemo(
    () => uniqueCountriesFromExhibitions([...exhibitions.upcoming, ...exhibitions.recent]).length,
    [exhibitions.upcoming, exhibitions.recent],
  );

  const scheduleJsonLd = useMemo(
    () =>
      exhibitions.status === 'ready' && exhibitions.upcoming.length > 0
        ? exhibitionsScheduleJsonLd(exhibitions.upcoming, { brandName })
        : null,
    [exhibitions.status, exhibitions.upcoming, brandName],
  );

  const whatsappHref = site.whatsapp_href || 'https://wa.me/919680384332';

  const upcomingTabActive = exhibitions.upcoming.length > 0 && hash !== '#exhibitions-past';
  const pastTabActive =
    exhibitions.recent.length > 0 &&
    (hash === '#exhibitions-past' || exhibitions.upcoming.length === 0);

  const nextShow = exhibitions.upcoming[0];

  return (
    <div className="exhibitions-page store-page store-page-enter">
      <PageSeoHelmet page="exhibitions">
        {scheduleJsonLd ? <script type="application/ld+json">{scheduleJsonLd}</script> : null}
      </PageSeoHelmet>

      <div className="container store-page__container exhibitions-page__container">
        <section className="exhibitions-hero shop-hero" aria-labelledby="exhibitions-page-title">
          <CatalogBreadcrumb
            align="start"
            className="exhibitions-page-breadcrumb catalog-breadcrumb--refined"
            items={[{ to: '/', label: 'Home' }, { label: 'Exhibitions' }]}
          />

          <div className="shop-hero-panel exhibitions-hero-panel text-center">
            {eyebrow ? <p className="shop-hero-eyebrow">{eyebrow}</p> : null}
            <h1 id="exhibitions-page-title" className="shop-page-title mb-2">
              {title}
            </h1>
            {subtitle ? (
              <p className="shop-page-subtitle shop-page-subtitle--editorial mb-0">{subtitle}</p>
            ) : null}
            {exhibitions.status === 'ready' && hasShows ? (
              <div className="exhibitions-page-hero-meta">
                {exhibitions.upcoming.length > 0 ? (
                  <span className="exhibitions-stat-pill">
                    {exhibitions.upcoming.length}{' '}
                    {exhibitions.upcoming.length === 1 ? 'upcoming show' : 'upcoming shows'}
                  </span>
                ) : null}
                {countryCount > 0 ? (
                  <span className="exhibitions-stat-pill exhibitions-stat-pill--muted">
                    {countryCount} {countryCount === 1 ? 'country' : 'countries'}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>

          {exhibitions.status === 'ready' && hasShows ? (
            <nav className="exhibitions-page-tabs" aria-label="Schedule sections">
              <div className="exhibitions-page-tabs__track" role="tablist">
                {exhibitions.upcoming.length > 0 ? (
                  <a
                    href="#exhibitions-upcoming"
                    className={`exhibitions-page-tabs__link${upcomingTabActive ? ' is-active' : ''}`}
                    role="tab"
                    aria-selected={upcomingTabActive}
                  >
                    Upcoming
                  </a>
                ) : null}
                {exhibitions.recent.length > 0 ? (
                  <a
                    href="#exhibitions-past"
                    className={`exhibitions-page-tabs__link${pastTabActive ? ' is-active' : ''}`}
                    role="tab"
                    aria-selected={pastTabActive}
                  >
                    Past
                  </a>
                ) : null}
              </div>
            </nav>
          ) : null}
        </section>

        {exhibitions.status === 'loading' ? (
          <StorePageLoading variant="exhibitions" message="Loading exhibition schedule…" />
        ) : null}

        {exhibitions.status === 'error' ? (
          <StorefrontEmptyState
            className="exhibitions-page-empty"
            title={store.exhibitions_error_title}
            text={store.exhibitions_error_text}
          >
            <button
              type="button"
              className="btn btn-primary store-cta rounded-pill"
              onClick={() => void exhibitions.refetch()}
            >
              {store.exhibitions_error_retry}
            </button>
            <Link to="/contact" className="btn btn-outline-secondary rounded-pill">
              {store.shop_contact_button}
            </Link>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-secondary rounded-pill"
            >
              {store.search_whatsapp_button}
            </a>
          </StorefrontEmptyState>
        ) : null}

        {exhibitions.status === 'ready' && nextShow ? (
          <section className="exhibitions-page-featured" aria-label="Next show">
            <ExhibitionSpotlight
              row={nextShow}
              brandName={brandName}
              featured
              badgeLabel={homeCopy.next_exhibition_badge || HOME_COPY_DEFAULTS.next_exhibition_badge}
            />
          </section>
        ) : null}

        {exhibitions.status === 'ready' && hasShows ? (
          <div className="exhibitions-page__schedule">
            <HomeExhibitions
              upcoming={exhibitions.upcoming}
              recent={exhibitions.recent}
              status={exhibitions.status}
              showHeader={false}
              pageMode
            />
          </div>
        ) : null}

        {exhibitions.status === 'ready' && !hasShows ? (
          <StorefrontEmptyState
            className="exhibitions-page-empty"
            title="No shows scheduled right now"
            text="We will post new dates here as soon as they are confirmed. You can still shop the full collection online."
          >
            <Link to="/shop" className="btn btn-primary store-cta rounded-pill">
              {store.search_browse_button}
            </Link>
            <Link to="/contact" className="btn btn-outline-secondary rounded-pill">
              {store.shop_contact_button}
            </Link>
            <a
              href={whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-secondary rounded-pill"
            >
              {store.search_whatsapp_button}
            </a>
          </StorefrontEmptyState>
        ) : null}

        {exhibitions.status === 'ready' && hasShows ? (
          <section className="exhibitions-page-cta utility-card" aria-labelledby="exhibitions-page-cta-title">
            <h2 id="exhibitions-page-cta-title" className="utility-card__title text-center">
              Can&apos;t make it to a show?
            </h2>
            <p className="exhibitions-page-cta__text text-center mx-auto mb-0">
              Shop the full collection online with delivery across India, or message us for custom orders and bulk gifting.
            </p>
            <div className="exhibitions-page-cta__actions">
              <Link to="/shop" className="btn btn-primary store-cta rounded-pill">
                Shop online
              </Link>
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-secondary rounded-pill"
              >
                {store.search_whatsapp_button}
              </a>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}
